import { ajaxService } from 'src/services/AjaxService';
import { IRequest, IResponse } from '../../system/interfaces';
import { IUserResponseDTO, IPractitionersCategoriesResponseDTO, ICollectionResponse } from './ResponseDtos';
import {
    IUserRequestDTO,
    IUpdatePractitionerRequestDTO,
    IInviteRequestDTO,
    ILanguageRequestDTO,
    ICreatePractitionerSpecializationRequestDTO,
} from './RequestDtos';

import config from '../../../config';

import { IUser } from '../interfaces';

import PractitionerModel from 'src/pods/user/models/PractitionerModel';
import TosModel from 'src/pods/user/models/TosModel';
import PaymentModel from 'src/pods/settings/models/PaymentModel';
import AddressModel from 'src/pods/settings/models/AdressModel';
import LanguageModel from '../models/LanguageModel';
import PractitionerSpecializationModel from '../models/PractitionerSpecializationModel';
import AvailabilityModel from '../../settings/models/AvailabilityModel';

export default class UserApiCalls {
    public static getUser() {
        return ajaxService.get<IResponse<IUserResponseDTO>>('/account/me/');
    }

    public static getUserDetails(id: number) {
        return ajaxService.get<IResponse<any>>(`/account/v1/users/${id}`);
    }

    public static updateUser(request: IRequest<IUserRequestDTO>) {
        return ajaxService.put<IResponse<any>>('/account/me/', request.payload);
    }

    public static inviteUser(request: IRequest<IInviteRequestDTO>) {
        return ajaxService.post<IResponse<any>>(`/account/me/invite`, request.payload);
    }

    public static workspaceInviteUser(request: IRequest<IInviteRequestDTO>) {
        return ajaxService.post<IResponse<any>>(`/account/me/workspace-invite`, request.payload, {
            headers: {
                'Redirect-URI': `${config.SSO_APP_URL}/confirm-invite/%s`,
            },
        });
    }

    public static getUsers(queryParams?: string) {
        return ajaxService.get<IResponse<any>>(`/account/v1/users/?${queryParams}`);
    }

    // practitioner
    public static getPractitioner() {
        return ajaxService.get<IResponse<PractitionerModel>>('/portal/me/');
    }

    public static editPractitioner(request: IRequest<IUpdatePractitionerRequestDTO>) {
        return ajaxService.put<IResponse<any>>('/portal/me/', request.payload);
    }

    public static getPractitionersCategories() {
        return ajaxService.get<IResponse<IPractitionersCategoriesResponseDTO>>('/portal/categories/');
    }

    public static getPractitionersCategoryById(categoryId: number) {
        return ajaxService.get<IResponse<IPractitionersCategoriesResponseDTO>>(`/portal/categories/${categoryId}`);
    }

    public static completeOnboarding(request: IRequest<any>) {
        return ajaxService.post<IResponse<any>>('/portal/me/', request.payload);
    }

    // languages
    public static getLanguages() {
        return ajaxService.get<IResponse<LanguageModel[]>>('/portal/languages/');
    }

    public static savePractitionerLanguage(request: IRequest<ILanguageRequestDTO>) {
        return ajaxService.post<IResponse<LanguageModel>>('/portal/me/languages/', request.payload);
    }

    public static deletePractitionerLanguage(languageID: number) {
        return ajaxService.delete<IResponse<any>>(`/portal/me/languages/${languageID}`);
    }

    // specializations
    public static createPractitionerSpecialization(request: IRequest<ICreatePractitionerSpecializationRequestDTO>) {
        return ajaxService.post<IResponse<PractitionerSpecializationModel>>(
            '/portal/me/specializations/',
            request.payload,
        );
    }

    public static deletePractitionerSpecialization(specializationID: number) {
        return ajaxService.delete<IResponse<any>>(`/portal/me/specializations/${specializationID}`);
    }

    // addresses
    public static getUserAddresses() {
        return ajaxService.get<IResponse<AddressModel[]>>('/portal/me/addresses/');
    }

    public static updateAddress(id: number, request: IRequest<Partial<AddressModel>>) {
        return ajaxService.put<IResponse<any>>(`/portal/me/addresses/${id}`, request.payload);
    }

    public static createAddress(request: IRequest<Partial<AddressModel>>) {
        return ajaxService.post<IResponse<AddressModel>>(`/portal/me/addresses/`, request.payload);
    }

    public static makeAddressPrimary(addressID: number) {
        return ajaxService.patch<IResponse<any>>(`/portal/me/addresses/${addressID}/primary/`);
    }

    public static getAllCollections() {
        return ajaxService.get<IResponse<ICollectionResponse>>('/account/collections/');
    }

    // availability
    public static getAvailability() {
        return ajaxService.get<IResponse<AvailabilityModel>>('/portal/me/availability/');
    }

    public static updateAvailability(id: number, data: Partial<AvailabilityModel>) {
        return ajaxService.put<IResponse<any>>(`/portal/me/availability/${id}`, data);
    }

    public static createAvailability(data: Partial<AvailabilityModel>) {
        return ajaxService.post<IResponse<AvailabilityModel>>(`/portal/me/availability/`, data);
    }

    // ToS
    public static getLastToS() {
        return ajaxService.get<IResponse<any>>('/account/tos', {
            noAuthentication: true,
        });
    }

    public static getLastPractitionerToS() {
        return ajaxService.get<IResponse<TosModel>>('/portal/me/tos/');
    }

    public static acceptLastCreatedPractitionerToS() {
        return ajaxService.put<IResponse<PractitionerModel>>('/portal/me/tos/');
    }

    public static acceptLastCreatedToS() {
        return ajaxService.put<IResponse<IUser>>('/account/me/tos');
    }

    // payments
    public static getPaymentDetails() {
        return ajaxService.get<IResponse<PaymentModel[]>>(`/portal/me/paymentDetails/`);
    }

    public static createPaymentDetails(request: IRequest<any>) {
        return ajaxService.post<IResponse<PaymentModel[]>>(`/portal/me/paymentDetails/`, request.payload);
    }

    public static updatePaymentDetails(request: IRequest<any>, id: number) {
        return ajaxService.put<IResponse<PaymentModel[]>>(`/portal/me/paymentDetails/${id}`, request.payload);
    }
}
